import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './CustomerProfile.module.css';
import masaLogo from '../Assets/MasaLogo.png';
import {
  buildCustomerProfileHtml,
  buildDealPrintHtml,
  buildSelectedDealsHtml,
  computeDealFinancials,
  openPrintWindow,
} from './printUtils';
import { getCustomerById, deleteDeal, updateCustomer, updateDeal } from '../api/customerApi';
import { getUsers } from '../api/authApi';
import { getTaxSetting } from '../api/settingsApi';
import { getProducts } from '../api/productApi';
import { useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

const translations = {
  en: {
    pageTitle: 'Customer Profile',
    overviewHeading: 'Customer Overview',
    dealsHeading: 'Recent Deals',
    searchDeals: 'Search deals',
    searchDealsPlaceholder: 'Search by deal ID, product name, or SKU',
    startDate: 'Start date',
    endDate: 'End date',
    taxFilter: 'Tax filter',
    taxFilterAll: 'All deals',
    taxFilterApplied: 'Taxes applied',
    taxFilterNotApplied: 'Taxes not applied',
    sortDeals: 'Sort deals by',
    sortNewest: 'Newest first',
    sortOldest: 'Oldest first',
    sortAmountHigh: 'Amount: high to low',
    sortAmountLow: 'Amount: low to high',
    noFilteredDeals: 'No deals match your filters yet.',
    clearFiltersAction: 'Clear filters',
    noDeals: 'No deals recorded yet.',
    contact: 'Contact Person',
    phone: 'Phone',
    jobTitle: 'Job Title',
    organization: 'Organization',
    businessActivity: 'Business Activity',
    address: 'Address',
    governorate: 'Governorate',
    mainArea: 'Main area',
    subArea: 'Sub area',
	    customerId: 'Customer ID',
	    fullName: 'Full Name',
	    assignedTo: 'Assigned Sales User',
	    editCustomer: 'Edit customer',
	    selectSalesUser: 'Select sales user',
	    unassigned: 'Unassigned',
	    deleteDeal: 'Delete deal',
    viewReceipt: 'View receipt',
    confirmDelete: 'Are you sure you want to delete this deal?',
    close: 'Close',
    homepage: 'Homepage',
    failedFetch: 'Failed to fetch customer profile. Please try again.',
	    failedDelete: 'Failed to delete deal. Please try again.',
	    failedUpdateCustomer: 'Failed to update customer. Please try again.',
	    failedLoadUsers: 'Failed to load sales users.',
    product: 'Product',
    sku: 'SKU',
    quantity: 'Quantity',
    price: 'Price',
    unitPrice: 'Unit price',
    pricePerQuantity: 'Price per quantity',
    productDiscount: 'Product discount (%)',
    selectProduct: 'Select product',
    selectSort: 'Select sort',
    selectColor: 'Select color',
    lockedPriceHint: 'Price is taken from the selected product sort.',
    dealDiscount: 'Deal discount (%)',
    applyTaxes: 'Apply tax',
    editDeal: 'Edit deal',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    validationError: 'Please fill all required fields correctly.',
    discountRangeError: 'Discount must be between 0 and 100.',
    dealDiscountRangeError: 'Deal discount must be between 0 and 100.',
    pricePerQuantityError: 'Price per quantity must be zero or more.',
    failedUpdateDeal: 'Failed to update deal. Please try again.',
    sort: 'Sort',
    color: 'Color',
    printDeal: 'Print deal',
    printProfile: 'Print profile',
    printSelectedDeals: 'Print selected deals',
    selectDeal: 'Select deal',
    printOptions: 'Print options',
    dealDate: 'Deal date',
    recipientName: 'Recipient name',
    recipientId: 'Recipient ID',
    print: 'Print',
    lineTotal: 'Line total',
    subtotal: 'Subtotal',
    totalDue: 'Total due',
    taxes: 'Taxes',
    taxNotApplied: 'Taxes not applied',
    signature: 'Signature',
    recipientDetails: 'Recipient details',
    customerDetails: 'Customer details',
    dealDetails: 'Deal details',
    productsTitle: 'Products',
    applyTaxesNote: 'Tax percentage is set from the backend and cannot be edited here.',
    notes: 'Notes',
    notesPlaceholder: 'Optional notes to include on the printout',
    noProducts: 'No products in this deal.',
    selectedDealsTitle: 'Selected deals',
    profileOverview: 'Profile overview',
    dealsOverview: 'Deals overview',
    totalDeals: 'Total deals',
    productsCount: 'Products count',
    totalAfterDiscount: 'Total after discount',
    totalBeforeTax: 'Total before tax',
    taxAmount: 'Tax amount',
    totalWithoutTax: 'Total without tax',
    totalWithTax: 'Total with tax',
    printCustomerProfileTitle: 'Customer profile',
    printedOn: 'Printed on',
    dealSummaryTitle: 'Deal summary',
    includeTaxes: 'Tax status',
    includeTaxesLabel: 'Include taxes',
    clearSelection: 'Clear selection',
    preparedBy: 'Prepared by',
    selectDealsPrompt: 'Select at least one deal to print.',
    pricePerQuantityShort: 'Price/Qty',
    selectedDealsEmpty: 'No deals selected.',
    dealTotals: 'Deal totals',
    dealId: 'Deal ID',
    showProducts: 'Show products',
    hideProducts: 'Hide products',
    showPayment: 'Show payment',
    hidePayment: 'Hide payment',
    discountStatus: 'Discount status',
    paymentStatus: 'Payment status',
    discountPending: 'Discount Pending',
    discountApproved: 'Discount Approved',
    discountRejected: 'Discount Rejected',
    discountNotRequested: 'No Discount',
    paymentUnpaid: 'Unpaid',
    paymentPartial: 'Partially Paid',
    paymentPaid: 'Paid',
    paymentTracking: 'Payment Tracking',
    frozenBeforeTax: 'Invoiced (excl. tax)',
    frozenAfterTax: 'Invoiced (incl. tax)',
    frozenTaxRate: 'Tax rate at deal time',
    amountDue: 'Amount due',
    paidAmount: 'Paid',
    remainingBalance: 'Remaining',
    taxMode: 'Collection mode',
    taxModeWith: 'With tax',
    taxModeWithout: 'Without tax',
  },
  ar: {
    pageTitle: 'ملف العميل',
    overviewHeading: 'نظرة عامة على العميل',
    dealsHeading: 'أحدث الصفقات',
    searchDeals: 'بحث في الصفقات',
    searchDealsPlaceholder: 'ابحث برقم الصفقة أو اسم المنتج أو الكود',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    taxFilter: 'تصفية الضريبة',
    taxFilterAll: 'جميع الصفقات',
    taxFilterApplied: 'الصفقات المطبق عليها الضريبة',
    taxFilterNotApplied: 'الصفقات بدون ضريبة',
    sortDeals: 'ترتيب الصفقات حسب',
    sortNewest: 'الأحدث أولاً',
    sortOldest: 'الأقدم أولاً',
    sortAmountHigh: 'القيمة: من الأعلى إلى الأقل',
    sortAmountLow: 'القيمة: من الأقل إلى الأعلى',
    noFilteredDeals: 'لا توجد صفقات مطابقة لمرشحات البحث.',
    clearFiltersAction: 'إعادة تعيين المرشحات',
    noDeals: 'لا توجد صفقات مسجلة بعد.',
    contact: 'مسؤول التواصل',
    phone: 'الهاتف',
    jobTitle: 'المسمى الوظيفي',
    organization: 'المؤسسة',
    businessActivity: 'النشاط التجاري',
    address: 'العنوان',
    governorate: 'المحافظة',
    mainArea: 'المنطقة الرئيسية',
    subArea: 'المنطقة الفرعية',
	    customerId: 'رقم العميل',
	    fullName: 'الاسم الكامل',
	    assignedTo: 'مندوب المبيعات المسؤول',
	    editCustomer: 'تعديل العميل',
	    selectSalesUser: 'اختر مندوب المبيعات',
	    unassigned: 'غير معين',
	    deleteDeal: 'حذف الصفقة',
    viewReceipt: 'عرض الإيصال',
    confirmDelete: 'هل أنت متأكد أنك تريد حذف هذه الصفقة؟',
    close: 'إغلاق',
    homepage: 'الصفحة الرئيسية',
    failedFetch: 'فشل في جلب ملف العميل. حاول مرة أخرى.',
	    failedDelete: 'فشل في حذف الصفقة. حاول مرة أخرى.',
	    failedUpdateCustomer: 'فشل في تحديث بيانات العميل. حاول مرة أخرى.',
	    failedLoadUsers: 'فشل تحميل مستخدمي المبيعات.',
    product: 'المنتج',
    sku: 'الرمز',
    quantity: 'الكمية',
    price: 'السعر',
    unitPrice: 'سعر الوحدة',
    pricePerQuantity: 'السعر لكل كمية',
    productDiscount: 'خصم المنتج (%)',
    selectProduct: 'اختر المنتج',
    selectSort: 'اختر الصنف',
    selectColor: 'اختر اللون',
    lockedPriceHint: 'يتم أخذ السعر من صنف المنتج المحدد.',
    dealDiscount: 'خصم الصفقة (%)',
    applyTaxes: 'تطبيق الضريبة',
    editDeal: 'تعديل الصفقة',
    saveChanges: 'حفظ التعديلات',
    cancel: 'إلغاء',
    validationError: 'يرجى إدخال جميع الحقول المطلوبة بشكل صحيح.',
    discountRangeError: 'يجب أن يكون الخصم بين 0 و100.',
    dealDiscountRangeError: 'يجب أن يكون خصم الصفقة بين 0 و100.',
    pricePerQuantityError: 'يجب أن يكون السعر لكل كمية أكبر من أو يساوي صفر.',
    failedUpdateDeal: 'فشل في تحديث الصفقة. حاول مرة أخرى.',
    sort: 'الفرز',
    color: 'اللون',
    printDeal: 'طباعة الصفقة',
    printProfile: 'طباعة الملف',
    printSelectedDeals: 'طباعة الصفقات المحددة',
    selectDeal: 'تحديد الصفقة',
    printOptions: 'خيارات الطباعة',
    dealDate: 'تاريخ الصفقة',
    recipientName: 'اسم المستلم',
    recipientId: 'رقم الهوية',
    print: 'طباعة',
    lineTotal: 'الإجمالي',
    subtotal: 'الإجمالي الفرعي',
    totalDue: 'الإجمالي المستحق',
    taxes: 'الضرائب',
    taxNotApplied: 'لا توجد ضرائب مطبقة',
    signature: 'التوقيع',
    recipientDetails: 'بيانات المستلم',
    customerDetails: 'بيانات العميل',
    dealDetails: 'بيانات الصفقة',
    productsTitle: 'المنتجات',
    applyTaxesNote: 'يتم تحديد نسبة الضريبة من الخادم ولا يمكن تعديلها هنا.',
    notes: 'ملاحظات',
    notesPlaceholder: 'ملاحظات اختيارية للطباعة',
    noProducts: 'لا توجد منتجات في هذه الصفقة.',
    selectedDealsTitle: 'الصفقات المحددة',
    profileOverview: 'نظرة عامة على الملف',
    dealsOverview: 'ملخص الصفقات',
    totalDeals: 'إجمالي الصفقات',
    productsCount: 'عدد المنتجات',
    totalAfterDiscount: 'الإجمالي بعد الخصم',
    totalBeforeTax: 'الإجمالي قبل الضريبة',
    taxAmount: 'قيمة الضريبة',
    totalWithoutTax: 'الإجمالي بدون ضريبة',
    totalWithTax: 'الإجمالي شامل الضريبة',
    printCustomerProfileTitle: 'ملف العميل',
    printedOn: 'تمت الطباعة في',
    dealSummaryTitle: 'ملخص الصفقة',
    includeTaxes: 'حالة الضرائب',
    includeTaxesLabel: 'تضمين الضرائب',
    clearSelection: 'مسح التحديد',
    preparedBy: 'أعد بواسطة',
    selectDealsPrompt: 'يرجى اختيار صفقة واحدة على الأقل للطباعة.',
    pricePerQuantityShort: 'السعر/كمية',
    selectedDealsEmpty: 'لا توجد صفقات محددة.',
    dealTotals: 'ملخص الصفقة',
    dealId: 'رقم الصفقة',
    showProducts: 'عرض المنتجات',
    hideProducts: 'إخفاء المنتجات',
    showPayment: 'عرض الدفع',
    hidePayment: 'إخفاء الدفع',
    discountStatus: 'حالة الخصم',
    paymentStatus: 'حالة الدفع',
    discountPending: 'خصم قيد الانتظار',
    discountApproved: 'خصم معتمد',
    discountRejected: 'خصم مرفوض',
    discountNotRequested: 'بدون خصم',
    paymentUnpaid: 'غير مدفوع',
    paymentPartial: 'مدفوع جزئيًا',
    paymentPaid: 'مدفوع',
    paymentTracking: 'تتبع المدفوعات',
    frozenBeforeTax: 'المُفوتَر (بدون ضريبة)',
    frozenAfterTax: 'المُفوتَر (شامل الضريبة)',
    frozenTaxRate: 'نسبة الضريبة وقت الصفقة',
    amountDue: 'المبلغ المستحق',
    paidAmount: 'المدفوع',
    remainingBalance: 'المتبقي',
    taxMode: 'وضع التحصيل',
    taxModeWith: 'شامل الضريبة',
    taxModeWithout: 'بدون ضريبة',
  },
};

const CustomerProfile = () => {
  const { id } = useParams();
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

	  const [customer, setCustomer] = useState(null);
	  const [products, setProducts] = useState([]);
	  const [salesUsers, setSalesUsers] = useState([]);
	  const [loading, setLoading] = useState(true);
	  const [errorMessage, setErrorMessage] = useState(null);
	  const [customerEditorState, setCustomerEditorState] = useState(null);
	  const [customerEditorError, setCustomerEditorError] = useState('');
	  const [customerEditorSubmitting, setCustomerEditorSubmitting] = useState(false);
	  const [confirmDealId, setConfirmDealId] = useState(null);
  const [dealEditorState, setDealEditorState] = useState(null);
  const [dealEditorError, setDealEditorError] = useState('');
  const [dealEditorSubmitting, setDealEditorSubmitting] = useState(false);
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [dealPrintState, setDealPrintState] = useState(null);
  const [dealPrintError, setDealPrintError] = useState('');
  const [dealSearchTerm, setDealSearchTerm] = useState('');
  const [dealDateFrom, setDealDateFrom] = useState('');
  const [dealDateTo, setDealDateTo] = useState('');
  const [dealTaxFilter, setDealTaxFilter] = useState('all');
  const [dealSortOption, setDealSortOption] = useState('newest');
  const [taxRatePercent, setTaxRatePercent] = useState(14);
  const [expandedDealSections, setExpandedDealSections] = useState({});

  const token = useMemo(() => localStorage.getItem('authToken'), []);
	  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
	    return { userType: null, requesterUsername: null };
	  }, [token]);

	  const canEditCustomer = ['admin', 'manager', 'sales-manager'].includes(userInfo.userType);

	  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const [customerResult, taxResult, productsResult] = await Promise.allSettled([
          getCustomerById(id),
          getTaxSetting(),
          getProducts(),
        ]);

        if (customerResult.status !== 'fulfilled') {
          throw customerResult.reason;
        }

        setCustomer(customerResult.value.data);

        if (taxResult.status === 'fulfilled') {
          const backendTaxRate = Number(taxResult.value.data?.taxRatePercent);
          if (Number.isFinite(backendTaxRate) && backendTaxRate >= 0) {
            setTaxRatePercent(backendTaxRate);
          }
        }

        if (productsResult?.status === 'fulfilled') {
          setProducts(productsResult.value.data || []);
        }
      } catch (error) {
        console.error('Error fetching customer profile:', error);
        setErrorMessage(t('failedFetch'));
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [id]);

	  useEffect(() => {
	    if (!canEditCustomer) {
	      return;
	    }

	    const fetchSalesUsers = async () => {
	      try {
	        const response = await getUsers({ roles: 'sales' });
	        const activeSalesUsers = (response.data || []).filter(
	          (user) => user.userType === 'sales' && user.isActive !== false
	        );
	        setSalesUsers(activeSalesUsers);
	      } catch (error) {
	        console.error('Error loading sales users:', error);
	        setErrorMessage(t('failedLoadUsers'));
	      }
	    };

	    fetchSalesUsers();
	    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [canEditCustomer]);

	  useEffect(() => {
	    if (!customer?.deals) return;
	    setSelectedDealIds((prev) =>
	      prev.filter((dealId) => customer.deals.some((deal) => deal._id === dealId))
	    );
	  }, [customer]);

	  const openCustomerEditor = () => {
	    if (!customer) return;
	    setCustomerEditorError('');
	    setCustomerEditorState({
	      fullName: customer.fullName || '',
	      organizationName: customer.organizationName || '',
	      businessActivity: customer.businessActivity || '',
	      governorate: customer.governorate || '',
	      mainArea: customer.mainArea || '',
	      subArea: customer.subArea || '',
	      address: customer.address || '',
	      contactPerson: customer.contactPerson || '',
	      jobTitle: customer.jobTitle || '',
	      phoneNumber: customer.phoneNumber || '',
	      salesUserId: customer.salesuser?._id || customer.salesuser || '',
	    });
	  };

	  const closeCustomerEditor = () => {
	    setCustomerEditorState(null);
	    setCustomerEditorError('');
	    setCustomerEditorSubmitting(false);
	  };

	  const updateCustomerEditorField = (field, value) => {
	    setCustomerEditorState((prev) => (prev ? { ...prev, [field]: value } : prev));
	  };

	  const handleCustomerEditorSubmit = async (event) => {
	    event.preventDefault();
	    if (!customerEditorState) return;

	    const requiredFields = [
	      'fullName',
	      'organizationName',
	      'businessActivity',
	      'governorate',
	      'mainArea',
	      'subArea',
	      'address',
	      'contactPerson',
	      'jobTitle',
	      'phoneNumber',
	    ];

	    const hasMissingRequiredField = requiredFields.some(
	      (field) => !String(customerEditorState[field] || '').trim()
	    );
	    if (hasMissingRequiredField) {
	      setCustomerEditorError(t('validationError'));
	      return;
	    }

	    const payload = requiredFields.reduce((result, field) => {
	      result[field] = String(customerEditorState[field] || '').trim();
	      return result;
	    }, {});
	    payload.salesUserId = customerEditorState.salesUserId || '';

	    setCustomerEditorSubmitting(true);
	    setCustomerEditorError('');
	    try {
	      const response = await updateCustomer(id, payload);
	      setCustomer(response.data);
	      closeCustomerEditor();
	    } catch (error) {
	      console.error('Error updating customer:', error);
	      setCustomerEditorError(
	        error?.response?.data?.message ||
	          error?.message ||
	          t('failedUpdateCustomer')
	      );
	    } finally {
	      setCustomerEditorSubmitting(false);
	    }
	  };

	  const handleDeleteDeal = async () => {
    if (!confirmDealId) return;
    try {
      await deleteDeal(id, confirmDealId);
      setCustomer((prev) => ({
        ...prev,
        deals: prev.deals.filter((deal) => deal._id !== confirmDealId),
      }));
      setConfirmDealId(null);
    } catch (error) {
      console.error('Error deleting deal:', error);
      setErrorMessage(t('failedDelete'));
      setConfirmDealId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return formatDateDDMMYYYY(value, '-');
    } catch (error) {
      return value;
    }
  };

  const getDealDateValue = useCallback((deal) => deal?.dealDate || deal?.createdAt, []);

  const toDateInputValue = useCallback((value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
  }, []);

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  const formatNumberValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value ?? '-';
    }
    try {
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
      }).format(numericValue);
    } catch (error) {
      return numericValue.toFixed(2);
    }
  };

  const formatCurrencyValue = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value ?? '-';
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2,
      }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} EGP`;
    }
  };

  const taxRateLabel = `${formatNumberValue(taxRatePercent)}%`;
  const taxTitle = `${t('taxes')} (${taxRateLabel})`;
  const applyTaxesLabel = `${t('applyTaxes')} (${taxRateLabel})`;

  const dealsWithFinancials = useMemo(() => {
    if (!Array.isArray(customer?.deals)) {
      return [];
    }
    return customer.deals.map((deal) => ({
      deal,
      financials: computeDealFinancials(deal, undefined, taxRatePercent),
    }));
  }, [customer, taxRatePercent]);

  const filteredDeals = useMemo(() => {
    const normalizedTerm = dealSearchTerm.trim().toLowerCase();
    const parsedStart = dealDateFrom ? new Date(dealDateFrom) : null;
    const parsedEnd = dealDateTo ? new Date(dealDateTo) : null;

    let startTimestamp = null;
    if (parsedStart && Number.isFinite(parsedStart.getTime())) {
      parsedStart.setHours(0, 0, 0, 0);
      startTimestamp = parsedStart.getTime();
    }

    let endTimestamp = null;
    if (parsedEnd && Number.isFinite(parsedEnd.getTime())) {
      parsedEnd.setHours(23, 59, 59, 999);
      endTimestamp = parsedEnd.getTime();
    }

    const includesTerm = (value) => {
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(normalizedTerm);
    };

    const getCreatedTime = (deal) => {
      const businessDate = getDealDateValue(deal);
      if (!businessDate) return null;
      const dateValue = new Date(businessDate);
      const timestamp = dateValue.getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    };

    const getTotalDue = (financials) => {
      const value = Number(financials?.totalDue);
      return Number.isFinite(value) ? value : 0;
    };

    return dealsWithFinancials
      .filter(({ deal }) => {
        if (normalizedTerm) {
          const baseMatch =
            includesTerm(deal._id) ||
            includesTerm(deal.dealReference) ||
            includesTerm(deal.dealName) ||
            includesTerm(deal.recipientName) ||
            includesTerm(deal.notes) ||
            includesTerm(deal.driveFileId);

          const products = Array.isArray(deal.products) ? deal.products : [];
          const productMatch = products.some((product) =>
            ['productName', 'SKU', 'sort', 'color'].some((key) =>
              includesTerm(product?.[key])
            )
          );

          if (!baseMatch && !productMatch) {
            return false;
          }
        }

        const createdTimestamp = getCreatedTime(deal);
        if (startTimestamp !== null) {
          if (createdTimestamp === null || createdTimestamp < startTimestamp) {
            return false;
          }
        }
        if (endTimestamp !== null) {
          if (createdTimestamp === null || createdTimestamp > endTimestamp) {
            return false;
          }
        }

        if (dealTaxFilter === 'taxed' && !deal.applyTaxes) {
          return false;
        }
        if (dealTaxFilter === 'untaxed' && deal.applyTaxes) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        switch (dealSortOption) {
          case 'oldest': {
            const first = getCreatedTime(a.deal) ?? 0;
            const second = getCreatedTime(b.deal) ?? 0;
            return first - second;
          }
          case 'amount-desc':
            return getTotalDue(b.financials) - getTotalDue(a.financials);
          case 'amount-asc':
            return getTotalDue(a.financials) - getTotalDue(b.financials);
          case 'newest':
          default: {
            const first = getCreatedTime(a.deal) ?? 0;
            const second = getCreatedTime(b.deal) ?? 0;
            return second - first;
          }
        }
      });
  }, [
    dealsWithFinancials,
    dealDateFrom,
    dealDateTo,
    dealSearchTerm,
    dealSortOption,
    dealTaxFilter,
    getDealDateValue,
  ]);

  const filtersActive =
    dealSearchTerm.trim() !== '' ||
    dealDateFrom !== '' ||
    dealDateTo !== '' ||
    dealTaxFilter !== 'all' ||
    dealSortOption !== 'newest';

  const hasDeals = dealsWithFinancials.length > 0;

  const clearDealFilters = () => {
    setDealSearchTerm('');
    setDealDateFrom('');
    setDealDateTo('');
    setDealTaxFilter('all');
    setDealSortOption('newest');
  };

  const renderDealProducts = (deal, financials) => {
    const products = financials?.products || [];
    if (products.length === 0) {
      return null;
    }
    return products.map((product, index) => (
      <div key={`${deal._id}-product-${index}`} className={styles.dealProductRow}>
        <span className={styles.dealProductName}>
          {product.productName}
          {[product.sort, product.color].some(Boolean) && (
            <span className={styles.dealProductMeta}>
              {[product.sort, product.color].filter(Boolean).join(' • ')}
            </span>
          )}
        </span>
        <span>{product.SKU}</span>
        <span>{formatNumberValue(product.quantity)}</span>
        <span>{formatCurrencyValue(product.unitPrice)}</span>
        <span>
          {product.pricePerQuantity > 0
            ? formatCurrencyValue(product.pricePerQuantity)
            : '-'}
        </span>
        <span>{`${formatNumberValue(product.discountPercent)}%`}</span>
        <span>{formatCurrencyValue(product.netAmount)}</span>
      </div>
    ));
  };

  const getDiscountBadge = (status) => {
    const map = {
      pending: { label: t('discountPending'), cls: styles.discountBadgePending },
      approved: { label: t('discountApproved'), cls: styles.discountBadgeApproved },
      rejected: { label: t('discountRejected'), cls: styles.discountBadgeRejected },
    };
    return map[status] || { label: t('discountNotRequested'), cls: styles.discountBadgeNone };
  };

  const getPaymentBadge = (status) => {
    const map = {
      paid: { label: t('paymentPaid'), cls: styles.paymentBadgePaid },
      'partially-paid': { label: t('paymentPartial'), cls: styles.paymentBadgePartial },
    };
    return map[status] || { label: t('paymentUnpaid'), cls: styles.paymentBadgeUnpaid };
  };

  const findCatalogProductBySku = useCallback(
    (sku) => products.find((item) => item.SKU === sku),
    [products]
  );

  const getDealEditorProduct = (line) =>
    products.find((item) => item._id === line.productId) ||
    findCatalogProductBySku(line.SKU);

  const getDealEditorSort = (line) =>
    getDealEditorProduct(line)?.sorts?.find((sort) => sort.sortName === line.sort);

  const openDealEditor = (deal) => {
    if (!deal) return;
    setDealEditorError('');
    setDealEditorState({
      dealId: deal._id,
      dealDate: toDateInputValue(getDealDateValue(deal)),
      products: (Array.isArray(deal.products) ? deal.products : []).map((product, index) => ({
        key: product._id || `${deal._id}-${index}`,
        productId: findCatalogProductBySku(product.SKU)?._id || '',
        productName: product.productName || '',
        SKU: product.SKU || '',
        sort: product.sort || '',
        color: product.color || '',
        quantity:
          product.quantity !== undefined && product.quantity !== null
            ? String(product.quantity)
            : '',
        pricePerItem:
          product.pricePerItem !== undefined && product.pricePerItem !== null
            ? String(product.pricePerItem)
            : '',
      })),
      dealDiscount:
        deal.dealDiscount !== undefined && deal.dealDiscount !== null
          ? String(deal.dealDiscount)
          : '0',
      applyTaxes: Boolean(deal.applyTaxes),
    });
  };

  const closeDealEditor = () => {
    setDealEditorState(null);
    setDealEditorError('');
    setDealEditorSubmitting(false);
  };

  const updateDealProductField = (index, field, value) => {
    setDealEditorState((prev) => {
      if (!prev) return prev;
      const updatedProducts = [...prev.products];
      const current = updatedProducts[index];

      if (field === 'productId') {
        const selectedProduct = products.find((item) => item._id === value);
        updatedProducts[index] = {
          ...current,
          productId: value,
          productName: selectedProduct?.name || '',
          SKU: selectedProduct?.SKU || '',
          sort: '',
          color: '',
          pricePerItem: '',
        };
      } else if (field === 'sort') {
        const selectedProduct =
          products.find((item) => item._id === current.productId) ||
          findCatalogProductBySku(current.SKU);
        const selectedSort = selectedProduct?.sorts?.find(
          (sort) => sort.sortName === value
        );
        updatedProducts[index] = {
          ...current,
          sort: value,
          color: '',
          pricePerItem:
            selectedSort?.price !== undefined && selectedSort?.price !== null
              ? String(selectedSort.price)
              : '',
        };
      } else {
        updatedProducts[index] = {
          ...current,
          [field]: value,
        };
      }
      return { ...prev, products: updatedProducts };
    });
  };

  const handleDealDiscountChange = (value) => {
    setDealEditorState((prev) => (prev ? { ...prev, dealDiscount: value } : prev));
  };

  const handleDealDateChange = (value) => {
    setDealEditorState((prev) => (prev ? { ...prev, dealDate: value } : prev));
  };

  const handleDealApplyTaxesToggle = () => {
    setDealEditorState((prev) =>
      prev ? { ...prev, applyTaxes: !prev.applyTaxes } : prev
    );
  };

  const handleDealEditorSubmit = async (event) => {
    event.preventDefault();
    if (!dealEditorState) return;
    setDealEditorError('');

    let formattedProducts;
    try {
      formattedProducts = dealEditorState.products.map((product) => {
        const quantity = Number(product.quantity);
        const selectedProduct =
          products.find((item) => item._id === product.productId) ||
          findCatalogProductBySku(product.SKU);
        const selectedSort = selectedProduct?.sorts?.find(
          (sort) => sort.sortName === product.sort
        );
        const selectedColor = selectedSort?.colors?.find(
          (color) => color.color === product.color
        );
        const pricePerItem = Number(selectedSort?.price);

        if (
          !selectedProduct ||
          !product.sort.trim() ||
          !selectedSort ||
          !product.color.trim() ||
          !selectedColor
        ) {
          throw new Error(t('validationError'));
        }
        if (
          Number.isNaN(quantity) ||
          Number.isNaN(pricePerItem) ||
          quantity <= 0 ||
          pricePerItem < 0
        ) {
          throw new Error(t('validationError'));
        }

        return {
          productName: selectedProduct.name,
          SKU: selectedProduct.SKU,
          quantity,
          sort: product.sort.trim(),
          color: product.color.trim(),
          pricePerItem,
          pricePerQuantity: 0,
          discount: 0,
        };
      });
    } catch (validationError) {
      setDealEditorError(
        validationError instanceof Error && validationError.message
          ? validationError.message
          : t('validationError')
      );
      return;
    }

    const dealDiscountValue =
      dealEditorState.dealDiscount === '' ? 0 : Number(dealEditorState.dealDiscount);
    if (!dealEditorState.dealDate) {
      setDealEditorError(t('validationError'));
      return;
    }
    if (
      Number.isNaN(dealDiscountValue) ||
      dealDiscountValue < 0 ||
      dealDiscountValue > 100
    ) {
      setDealEditorError(t('dealDiscountRangeError'));
      return;
    }

    const formData = new FormData();
    formData.append('products', JSON.stringify(formattedProducts));
    formData.append('dealDate', dealEditorState.dealDate);
    formData.append('dealDiscount', String(dealDiscountValue));
    formData.append('applyTaxes', dealEditorState.applyTaxes ? 'true' : 'false');

    setDealEditorSubmitting(true);
    try {
      const response = await updateDeal(id, dealEditorState.dealId, formData);
      setCustomer(response.data);
      closeDealEditor();
    } catch (error) {
      console.error('Error updating deal:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('failedUpdateDeal');
      setDealEditorError(message);
    } finally {
      setDealEditorSubmitting(false);
    }
  };

  const toggleDealSelection = (dealId) => {
    setSelectedDealIds((prev) =>
      prev.includes(dealId)
        ? prev.filter((id) => id !== dealId)
        : [...prev, dealId]
    );
  };

  const clearDealSelection = () => setSelectedDealIds([]);

  const canManageDeals = ['admin', 'manager', 'sales-manager'].includes(userInfo.userType);

  const isDealSectionExpanded = (dealId, section) =>
    Boolean(expandedDealSections[dealId]?.[section]);

  const toggleDealSection = (dealId, section) => {
    setExpandedDealSections((prev) => ({
      ...prev,
      [dealId]: {
        ...prev[dealId],
        [section]: !prev[dealId]?.[section],
      },
    }));
  };

  const printLabels = useMemo(() => {
    const current = translations[language];
    return {
      dealSummaryTitle: current.dealSummaryTitle,
      printedOn: current.printedOn,
      customerDetails: current.customerDetails,
      customerId: current.customerId,
      organization: current.organization,
      contact: current.contact,
      phone: current.phone,
      address: current.address,
      dealDetails: current.dealDetails,
      dealDate: current.dealDate,
      preparedBy: current.preparedBy,
      includeTaxes: current.includeTaxes,
      taxes: taxTitle,
      taxNotApplied: current.taxNotApplied,
      productsTitle: current.productsTitle,
      product: current.product,
      sku: current.sku,
      sort: current.sort,
      color: current.color,
      quantity: current.quantity,
      unitPrice: current.unitPrice,
      pricePerQuantityShort: current.pricePerQuantityShort,
      discount: current.productDiscount,
      lineTotal: current.lineTotal,
      dealTotals: current.dealTotals,
      applyTaxesNote: `${current.applyTaxesNote} (${taxRateLabel})`,
      recipientDetails: current.recipientDetails,
      recipientName: current.recipientName,
      recipientId: current.recipientId,
      signature: current.signature,
      notes: current.notes,
      noProducts: current.noProducts,
      selectedDealsTitle: current.selectedDealsTitle,
      profileOverview: current.profileOverview,
      dealsOverview: current.dealsOverview,
      totalDeals: current.totalDeals,
      productsCount: current.productsCount,
      totalAfterDiscount: current.totalAfterDiscount,
      totalBeforeTax: current.totalBeforeTax,
      taxAmount: current.taxAmount,
      totalWithoutTax: current.totalWithoutTax,
      totalWithTax: current.totalWithTax,
      totalDue: current.totalDue,
      subtotal: current.subtotal,
    };
  }, [language, taxRateLabel, taxTitle]);

  const openDealPrintModal = (deal) => {
    if (!deal) return;
    setDealPrintError('');
    setDealPrintState({
      dealId: deal._id,
      date: toDateInputValue(getDealDateValue(deal)) || new Date().toISOString().split('T')[0],
      recipientName: '',
      recipientId: '',
      applyTaxes: Boolean(deal.applyTaxes),
      notes: '',
    });
  };

  const closeDealPrintModal = () => {
    setDealPrintState(null);
    setDealPrintError('');
  };

  const updateDealPrintState = (field, value) => {
    setDealPrintState((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleDealPrintSubmit = (event) => {
    event.preventDefault();
    if (!dealPrintState) return;

    if (!dealPrintState.date) {
      setDealPrintError(t('validationError'));
      return;
    }

    const currentDeal = customer?.deals?.find(
      (item) => item._id === dealPrintState.dealId
    );

    if (!currentDeal) {
      setDealPrintError(t('failedFetch'));
      return;
    }

    const html = buildDealPrintHtml({
      customer,
      deal: currentDeal,
      options: {
        printDate: dealPrintState.date,
        recipientName: dealPrintState.recipientName,
        recipientId: dealPrintState.recipientId,
        notes: dealPrintState.notes,
        applyTaxes: dealPrintState.applyTaxes,
        taxRatePercent,
        preparedBy: userInfo.requesterUsername,
      },
      labels: printLabels,
      logoSrc: masaLogo,
      locale,
      taxRatePercent,
    });
    openPrintWindow({ title: t('printDeal'), content: html });
    closeDealPrintModal();
  };

  const handlePrintSelectedDeals = () => {
    if (!customer) return;
    if (selectedDealIds.length === 0) {
      setErrorMessage(t('selectDealsPrompt'));
      return;
    }

    const dealsToPrint = Array.isArray(customer.deals)
      ? customer.deals.filter((deal) => selectedDealIds.includes(deal._id))
      : [];

    if (dealsToPrint.length === 0) {
      setErrorMessage(t('selectedDealsEmpty'));
      return;
    }

    const html = buildSelectedDealsHtml({
      customer,
      deals: dealsToPrint,
      labels: printLabels,
      logoSrc: masaLogo,
      locale,
      taxRatePercent,
    });
    openPrintWindow({ title: t('printSelectedDeals'), content: html });
  };

  const handlePrintProfile = () => {
    if (!customer) return;
    const overviewEntries = [
      { label: t('customerId'), value: customer.customerID },
      { label: t('organization'), value: customer.organizationName },
      { label: t('businessActivity'), value: customer.businessActivity },
      { label: t('governorate'), value: customer.governorate },
      { label: t('mainArea'), value: customer.mainArea },
      { label: t('subArea'), value: customer.subArea },
      { label: t('address'), value: customer.address },
      { label: t('contact'), value: customer.contactPerson },
      { label: t('phone'), value: customer.phoneNumber },
      { label: t('jobTitle'), value: customer.jobTitle },
    ];

    if (customer.salesuser?.username) {
      overviewEntries.push({
        label: t('assignedTo'),
        value: customer.salesuser.username,
      });
    }

    const html = buildCustomerProfileHtml({
      customer,
      overviewEntries,
      labels: printLabels,
      logoSrc: masaLogo,
      locale,
    });
    openPrintWindow({ title: t('printProfile'), content: html });
  };

  if (loading) {
    return (
      <AppLayout
        title={t('pageTitle')}
        contentClassName={styles.customerProfilePage}
        contentProps={{ 'aria-busy': true }}
      >
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      </AppLayout>
    );
  }

  if (!customer) {
    return (
      <AppLayout
        title={t('pageTitle')}
        contentClassName={styles.customerProfilePage}
      >
        <section className={styles.emptyState}>{t('failedFetch')}</section>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.customerProfilePage}
    >
	      <section className={styles.overviewCard}>
	        <div className={styles.overviewHeader}>
	          <h2 className={styles.sectionTitle}>{t('overviewHeading')}</h2>
	          {canEditCustomer && (
	            <button
	              type="button"
	              className={styles.secondaryButton}
	              onClick={openCustomerEditor}
	            >
	              {t('editCustomer')}
	            </button>
	          )}
	        </div>
	        <div className={styles.overviewGrid}>
	          <div className={styles.overviewItem}>
	            <span className={styles.overviewLabel}>{t('customerId')}</span>
	            <span className={styles.overviewValue}>{customer.customerID}</span>
	          </div>
	          <div className={styles.overviewItem}>
	            <span className={styles.overviewLabel}>{t('fullName')}</span>
	            <span className={styles.overviewValue}>{customer.fullName}</span>
	          </div>
	          <div className={styles.overviewItem}>
	            <span className={styles.overviewLabel}>{t('organization')}</span>
	            <span className={styles.overviewValue}>{customer.organizationName}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('businessActivity')}</span>
            <span className={styles.overviewValue}>{customer.businessActivity}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('address')}</span>
            <span className={styles.overviewValue}>{customer.address}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('governorate')}</span>
            <span className={styles.overviewValue}>{customer.governorate}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('mainArea')}</span>
            <span className={styles.overviewValue}>{customer.mainArea}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('subArea')}</span>
            <span className={styles.overviewValue}>{customer.subArea}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('contact')}</span>
            <span className={styles.overviewValue}>{customer.contactPerson}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('phone')}</span>
            <span className={styles.overviewValue}>{customer.phoneNumber}</span>
          </div>
          <div className={styles.overviewItem}>
            <span className={styles.overviewLabel}>{t('jobTitle')}</span>
            <span className={styles.overviewValue}>{customer.jobTitle}</span>
          </div>
	          <div className={styles.overviewItem}>
	            <span className={styles.overviewLabel}>{t('assignedTo')}</span>
	            <span className={styles.overviewValue}>
	              {customer.salesuser?.username || t('unassigned')}
	            </span>
	          </div>
	        </div>
	      </section>

      <section className={styles.dealsSection}>
        <header className={styles.dealsHeader}>
          <h2 className={styles.sectionTitle}>{t('dealsHeading')}</h2>
          <div className={styles.dealsHeaderActions}>
            {selectedDealIds.length > 0 && (
              <span className={styles.dealsSelectionInfo}>
                {`${selectedDealIds.length} ${t('selectedDealsTitle')}`}
              </span>
            )}
            <button type="button" className={styles.secondaryButton} onClick={handlePrintProfile}>
              {t('printProfile')}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handlePrintSelectedDeals}
              disabled={selectedDealIds.length === 0}
            >
              {t('printSelectedDeals')}
            </button>
            {selectedDealIds.length > 0 && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={clearDealSelection}
              >
                {t('clearSelection')}
              </button>
            )}
          </div>
        </header>

        {hasDeals ? (
          <>
            <div className={styles.dealsFilters}>
              <label className={`${styles.inputGroup} ${styles.searchControl}`}>
                <span>{t('searchDeals')}</span>
                <input
                  type="search"
                  value={dealSearchTerm}
                  onChange={(event) => setDealSearchTerm(event.target.value)}
                  placeholder={t('searchDealsPlaceholder')}
                  className={styles.inputField}
                />
              </label>
              <label className={styles.inputGroup}>
                <span>{t('startDate')}</span>
                <input
                  type="date"
                  value={dealDateFrom}
                  onChange={(event) => setDealDateFrom(event.target.value)}
                  className={styles.inputField}
                  max={dealDateTo || undefined}
                />
              </label>
              <label className={styles.inputGroup}>
                <span>{t('endDate')}</span>
                <input
                  type="date"
                  value={dealDateTo}
                  onChange={(event) => setDealDateTo(event.target.value)}
                  className={styles.inputField}
                  min={dealDateFrom || undefined}
                />
              </label>
              <label className={styles.inputGroup}>
                <span>{t('taxFilter')}</span>
                <select
                  className={styles.inputField}
                  value={dealTaxFilter}
                  onChange={(event) => setDealTaxFilter(event.target.value)}
                >
                  <option value="all">{t('taxFilterAll')}</option>
                  <option value="taxed">{t('taxFilterApplied')}</option>
                  <option value="untaxed">{t('taxFilterNotApplied')}</option>
                </select>
              </label>
              <label className={styles.inputGroup}>
                <span>{t('sortDeals')}</span>
                <select
                  className={styles.inputField}
                  value={dealSortOption}
                  onChange={(event) => setDealSortOption(event.target.value)}
                >
                  <option value="newest">{t('sortNewest')}</option>
                  <option value="oldest">{t('sortOldest')}</option>
                  <option value="amount-desc">{t('sortAmountHigh')}</option>
                  <option value="amount-asc">{t('sortAmountLow')}</option>
                </select>
              </label>
              <div className={styles.filterActions}>
                <button
                  type="button"
                  className={styles.clearFiltersButton}
                  onClick={clearDealFilters}
                  disabled={!filtersActive}
                >
                  {t('clearFiltersAction')}
                </button>
              </div>
            </div>

            {filteredDeals.length > 0 ? (
              <div className={styles.dealsGrid}>
                {filteredDeals.map(({ deal, financials }) => {
                  const discBadge = getDiscountBadge(deal.discountApprovalStatus);
                  const payBadge = getPaymentBadge(deal.paymentStatus);
                  const productsExpanded = isDealSectionExpanded(deal._id, 'products');
                  const paymentExpanded = isDealSectionExpanded(deal._id, 'payment');
                  const hasPaymentSnapshot = deal.amountBeforeTax > 0;
                  const amountDue = hasPaymentSnapshot
                    ? deal.taxInclusive
                      ? deal.amountAfterTax
                      : deal.amountBeforeTax
                    : financials.totalDue;
                  const remaining = Math.max(0, amountDue - (deal.paidAmount || 0));

                  return (
                    <article key={deal._id} className={styles.dealCard}>
                      <div className={styles.dealHeader}>
                        <div className={styles.dealHeaderInfo}>
                          <label className={styles.dealSelect}>
                            <input
                              type="checkbox"
                              checked={selectedDealIds.includes(deal._id)}
                              onChange={() => toggleDealSelection(deal._id)}
                            />
                            <span>{t('selectDeal')}</span>
                          </label>
                          <div className={styles.dealIdentity}>
                            <span className={styles.dealId}>
                              {t('dealId')}: #{String(deal.dealReference || deal._id).slice(-8)}
                            </span>
                            <span className={styles.dealDate}>{formatDate(getDealDateValue(deal))}</span>
                          </div>
                          <div className={styles.dealBadgeRow}>
                            <span className={`${styles.dealStatusBadge} ${discBadge.cls}`}>
                              {t('discountStatus')}: {discBadge.label}
                            </span>
                            <span className={`${styles.dealStatusBadge} ${payBadge.cls}`}>
                              {t('paymentStatus')}: {payBadge.label}
                            </span>
                          </div>
                        </div>
                        <div className={styles.dealActions}>
                          {deal.driveFileId && (
                            <a
                              className={styles.primaryButton}
                              href={`https://drive.google.com/file/d/${deal.driveFileId}/view`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t('viewReceipt')}
                            </a>
                          )}
                          <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => openDealPrintModal(deal)}
                          >
                            {t('printDeal')}
                          </button>
                          {canManageDeals && (
                            <>
                              <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => openDealEditor(deal)}
                              >
                                {t('editDeal')}
                              </button>
                              <button
                                type="button"
                                className={styles.dangerButton}
                                onClick={() => setConfirmDealId(deal._id)}
                              >
                                {t('deleteDeal')}
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div className={styles.dealSummary}>
                        <div>
                          <span className={styles.dealSummaryLabel}>{t('subtotal')}</span>
                          <span className={styles.dealSummaryValue}>
                            {formatCurrencyValue(financials.subtotal)}
                          </span>
                        </div>
                        <div>
                          <span className={styles.dealSummaryLabel}>{t('dealDiscount')}</span>
                          <span className={styles.dealSummaryValue}>
                            {`${formatNumberValue(financials.dealDiscountPercent)}% (${formatCurrencyValue(
                              financials.dealDiscountAmount
                            )})`}
                          </span>
                        </div>
                        <div>
                          <span className={styles.dealSummaryLabel}>{t('totalBeforeTax')}</span>
                          <span className={styles.dealSummaryValue}>
                            {formatCurrencyValue(financials.subtotalAfterDiscount)}
                          </span>
                        </div>
                        <div>
                          <span className={styles.dealSummaryLabel}>{t('taxAmount')}</span>
                          <span className={styles.dealSummaryValue}>
                            {financials.applyTaxes
                              ? formatCurrencyValue(financials.taxAmount)
                              : t('taxNotApplied')}
                          </span>
                        </div>
                        <div>
                          <span className={styles.dealSummaryLabel}>{t('totalWithoutTax')}</span>
                          <span className={styles.dealSummaryValue}>
                            {formatCurrencyValue(financials.totalWithoutTax)}
                          </span>
                        </div>
                        <div>
                          <span className={styles.dealSummaryLabel}>{taxTitle}</span>
                          <span className={styles.dealSummaryValue}>
                            {formatCurrencyValue(financials.totalWithTax)}
                          </span>
                        </div>
                        <div className={styles.dealSummaryHighlight}>
                          <span className={styles.dealSummaryLabel}>{t('totalDue')}</span>
                          <span className={styles.dealSummaryValue}>
                            {formatCurrencyValue(financials.totalDue)}
                          </span>
                        </div>
                      </div>

                      <div className={styles.dealSectionControls}>
                        <button
                          type="button"
                          className={styles.dealToggleButton}
                          onClick={() => toggleDealSection(deal._id, 'products')}
                        >
                          {productsExpanded ? t('hideProducts') : t('showProducts')}
                        </button>
                        {hasPaymentSnapshot && (
                          <button
                            type="button"
                            className={styles.dealToggleButton}
                            onClick={() => toggleDealSection(deal._id, 'payment')}
                          >
                            {paymentExpanded ? t('hidePayment') : t('showPayment')}
                          </button>
                        )}
                      </div>

                      {productsExpanded && (
                        <div className={styles.dealProducts}>
                          <div className={styles.dealProductHeader}>
                            <span>{t('product')}</span>
                            <span>{t('sku')}</span>
                            <span>{t('quantity')}</span>
                            <span>{t('unitPrice')}</span>
                            <span>{t('pricePerQuantityShort')}</span>
                            <span>{t('productDiscount')}</span>
                            <span>{t('lineTotal')}</span>
                          </div>
                          {renderDealProducts(deal, financials)}
                        </div>
                      )}

                      {hasPaymentSnapshot && paymentExpanded && (
                        <div className={styles.paymentTrackingPanel}>
                          <span className={styles.paymentTrackingTitle}>{t('paymentTracking')}</span>
                          <div className={styles.paymentTrackingGrid}>
                            <div>
                              <span className={styles.dealSummaryLabel}>{t('frozenBeforeTax')}</span>
                              <span className={styles.dealSummaryValue}>{formatCurrencyValue(deal.amountBeforeTax)}</span>
                            </div>
                            <div>
                              <span className={styles.dealSummaryLabel}>{t('frozenAfterTax')} ({deal.taxRateSnapshot}%)</span>
                              <span className={styles.dealSummaryValue}>{formatCurrencyValue(deal.amountAfterTax)}</span>
                            </div>
                            <div>
                              <span className={styles.dealSummaryLabel}>{t('taxMode')}</span>
                              <span className={styles.dealSummaryValue}>
                                {deal.taxInclusive ? t('taxModeWith') : t('taxModeWithout')}
                              </span>
                            </div>
                            <div>
                              <span className={styles.dealSummaryLabel}>{t('amountDue')}</span>
                              <span className={styles.dealSummaryValue}>{formatCurrencyValue(amountDue)}</span>
                            </div>
                            <div>
                              <span className={styles.dealSummaryLabel}>{t('paidAmount')}</span>
                              <span className={`${styles.dealSummaryValue} ${styles.paymentTrackingPaid}`}>{formatCurrencyValue(deal.paidAmount || 0)}</span>
                            </div>
                            <div className={remaining > 0 ? styles.dealSummaryHighlight : undefined}>
                              <span className={styles.dealSummaryLabel}>{t('remainingBalance')}</span>
                              <span className={`${styles.dealSummaryValue} ${remaining > 0 ? styles.paymentTrackingRemaining : styles.paymentTrackingPaid}`}>
                                {formatCurrencyValue(remaining)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className={styles.filteredEmptyState}>{t('noFilteredDeals')}</div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>{t('noDeals')}</div>
        )}
      </section>

	      {customerEditorState && (
	        <div className={styles.modalBackdrop}>
	          <div className={styles.largeModalContent}>
	            <h3 className={styles.modalTitle}>{t('editCustomer')}</h3>
	            <form className={styles.dealEditorForm} onSubmit={handleCustomerEditorSubmit}>
	              <div className={styles.customerEditorGrid}>
	                <label className={styles.inputGroup}>
	                  <span>{t('customerId')}</span>
	                  <input
	                    type="text"
	                    value={customer.customerID || ''}
	                    className={styles.inputField}
	                    readOnly
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('fullName')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.fullName}
	                    onChange={(event) =>
	                      updateCustomerEditorField('fullName', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('organization')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.organizationName}
	                    onChange={(event) =>
	                      updateCustomerEditorField('organizationName', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('businessActivity')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.businessActivity}
	                    onChange={(event) =>
	                      updateCustomerEditorField('businessActivity', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('governorate')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.governorate}
	                    onChange={(event) =>
	                      updateCustomerEditorField('governorate', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('mainArea')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.mainArea}
	                    onChange={(event) =>
	                      updateCustomerEditorField('mainArea', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('subArea')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.subArea}
	                    onChange={(event) =>
	                      updateCustomerEditorField('subArea', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('address')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.address}
	                    onChange={(event) =>
	                      updateCustomerEditorField('address', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('contact')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.contactPerson}
	                    onChange={(event) =>
	                      updateCustomerEditorField('contactPerson', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('jobTitle')}</span>
	                  <input
	                    type="text"
	                    value={customerEditorState.jobTitle}
	                    onChange={(event) =>
	                      updateCustomerEditorField('jobTitle', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('phone')}</span>
	                  <input
	                    type="tel"
	                    value={customerEditorState.phoneNumber}
	                    onChange={(event) =>
	                      updateCustomerEditorField('phoneNumber', event.target.value)
	                    }
	                    className={styles.inputField}
	                    required
	                  />
	                </label>
	                <label className={styles.inputGroup}>
	                  <span>{t('assignedTo')}</span>
	                  <select
	                    value={customerEditorState.salesUserId}
	                    onChange={(event) =>
	                      updateCustomerEditorField('salesUserId', event.target.value)
	                    }
	                    className={styles.inputField}
	                  >
	                    <option value="">{t('unassigned')}</option>
	                    {salesUsers.map((salesUser) => (
	                      <option key={salesUser._id} value={salesUser._id}>
	                        {salesUser.username}
	                      </option>
	                    ))}
	                  </select>
	                </label>
	              </div>

	              {customerEditorError && (
	                <p className={styles.errorText} role="alert">
	                  {customerEditorError}
	                </p>
	              )}

	              <div className={styles.modalButtons}>
	                <button
	                  type="submit"
	                  className={styles.primaryButton}
	                  disabled={customerEditorSubmitting}
	                >
	                  {t('saveChanges')}
	                </button>
	                <button
	                  type="button"
	                  className={styles.secondaryButton}
	                  onClick={closeCustomerEditor}
	                >
	                  {t('cancel')}
	                </button>
	              </div>
	            </form>
	          </div>
	        </div>
	      )}

	      {dealEditorState && (
	        <div className={styles.modalBackdrop}>
          <div className={styles.largeModalContent}>
            <h3 className={styles.modalTitle}>{t('editDeal')}</h3>
            <form className={styles.dealEditorForm} onSubmit={handleDealEditorSubmit}>
              <div className={styles.dealEditorProducts}>
                {dealEditorState.products.map((product, index) => {
                  const selectedEditorProduct = getDealEditorProduct(product);
                  const selectedEditorSort = getDealEditorSort(product);
                  return (
                    <div key={product.key || index} className={styles.dealProductEditor}>
                      <div className={styles.dealProductEditorTitle}>
                        {product.productName || `${t('product')} ${index + 1}`}
                      </div>
                      <div className={styles.dealProductEditorGrid}>
                        <label className={styles.inputGroup}>
                          <span>{t('product')}</span>
                          <select
                            value={product.productId}
                            onChange={(event) =>
                              updateDealProductField(index, 'productId', event.target.value)
                            }
                            className={styles.inputField}
                            required
                          >
                            <option value="">{t('selectProduct')}</option>
                            {products.map((catalogProduct) => (
                              <option key={catalogProduct._id} value={catalogProduct._id}>
                                {catalogProduct.name} ({catalogProduct.SKU})
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className={styles.inputGroup}>
                          <span>{t('sku')}</span>
                          <input
                            type="text"
                            value={product.SKU}
                            className={styles.inputField}
                            readOnly
                          />
                        </label>
                        <label className={styles.inputGroup}>
                          <span>{t('sort')}</span>
                          <select
                            value={product.sort}
                            onChange={(event) =>
                              updateDealProductField(index, 'sort', event.target.value)
                            }
                            className={styles.inputField}
                            required
                            disabled={!selectedEditorProduct}
                          >
                            <option value="">{t('selectSort')}</option>
                            {(selectedEditorProduct?.sorts || []).map((sort) => (
                              <option key={sort.sortName} value={sort.sortName}>
                                {sort.sortName}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className={styles.inputGroup}>
                          <span>{t('color')}</span>
                          <select
                            value={product.color}
                            onChange={(event) =>
                              updateDealProductField(index, 'color', event.target.value)
                            }
                            className={styles.inputField}
                            required
                            disabled={!selectedEditorSort}
                          >
                            <option value="">{t('selectColor')}</option>
                            {(selectedEditorSort?.colors || []).map((colorOption) => (
                              <option key={colorOption.color} value={colorOption.color}>
                                {colorOption.color}
                              </option>
                            ))}
                          </select>
                        </label>
                      <label className={styles.inputGroup}>
                        <span>{t('quantity')}</span>
                        <input
                          type="number"
                          min="0"
                          value={product.quantity}
                          onChange={(event) =>
                            updateDealProductField(index, 'quantity', event.target.value)
                          }
                          className={styles.inputField}
                          required
                        />
                      </label>
                      <label className={styles.inputGroup}>
                        <span>{t('unitPrice')}</span>
                        <input
                          type="text"
                          value={product.pricePerItem}
                          className={styles.inputField}
                          readOnly
                        />
                        <small className={styles.inputHint}>{t('lockedPriceHint')}</small>
                      </label>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className={styles.dealEditorSummary}>
                <label className={styles.inputGroup}>
                  <span>{t('dealDate')}</span>
                  <input
                    type="date"
                    value={dealEditorState.dealDate}
                    onChange={(event) => handleDealDateChange(event.target.value)}
                    className={styles.inputField}
                    required
                  />
                </label>

                {userInfo.userType !== 'sales' && (
                  <label className={styles.inputGroup}>
                    <span>{t('dealDiscount')}</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={dealEditorState.dealDiscount}
                      onChange={(event) => handleDealDiscountChange(event.target.value)}
                      className={styles.inputField}
                    />
                  </label>
                )}

                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={dealEditorState.applyTaxes}
                    onChange={handleDealApplyTaxesToggle}
                  />
                  <span>{applyTaxesLabel}</span>
                </label>
              </div>

              {dealEditorError && (
                <p className={styles.errorText} role="alert">
                  {dealEditorError}
                </p>
              )}

              <div className={styles.modalButtons}>
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={dealEditorSubmitting}
                >
                  {t('saveChanges')}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={closeDealEditor}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dealPrintState && (
        <div className={styles.modalBackdrop}>
          <div className={styles.largeModalContent}>
            <h3 className={styles.modalTitle}>{t('printOptions')}</h3>
            <form className={styles.dealEditorForm} onSubmit={handleDealPrintSubmit}>
              <div className={styles.dealPrintGrid}>
                <label className={styles.inputGroup}>
                  <span>{t('dealDate')}</span>
                  <input
                    type="date"
                    value={dealPrintState.date}
                    onChange={(event) => updateDealPrintState('date', event.target.value)}
                    className={styles.inputField}
                    required
                  />
                </label>
                <label className={styles.inputGroup}>
                  <span>{t('recipientName')}</span>
                  <input
                    type="text"
                    value={dealPrintState.recipientName}
                    onChange={(event) =>
                      updateDealPrintState('recipientName', event.target.value)
                    }
                    className={styles.inputField}
                  />
                </label>
                <label className={styles.inputGroup}>
                  <span>{t('recipientId')}</span>
                  <input
                    type="text"
                    value={dealPrintState.recipientId}
                    onChange={(event) =>
                      updateDealPrintState('recipientId', event.target.value)
                    }
                    className={styles.inputField}
                  />
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={dealPrintState.applyTaxes}
                    onChange={(event) =>
                      updateDealPrintState('applyTaxes', event.target.checked)
                    }
                  />
                  <span>{`${t('includeTaxesLabel')} (${taxRateLabel})`}</span>
                </label>
                <label className={styles.inputGroup}>
                  <span>{t('notes')}</span>
                  <textarea
                    rows="3"
                    value={dealPrintState.notes}
                    onChange={(event) => updateDealPrintState('notes', event.target.value)}
                    className={styles.inputField}
                    placeholder={t('notesPlaceholder')}
                  />
                </label>
              </div>
              {t('applyTaxesNote') && (
                <p className={styles.printHint}>{`${t('applyTaxesNote')} (${taxRateLabel})`}</p>
              )}
              {dealPrintError && (
                <p className={styles.errorText} role="alert">
                  {dealPrintError}
                </p>
              )}
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.primaryButton}>
                  {t('print')}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={closeDealPrintModal}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDealId && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{t('confirmDelete')}</p>
            <div className={styles.modalButtons}>
              <button className={styles.primaryButton} onClick={handleDeleteDeal}>
                {t('deleteDeal')}
              </button>
              <button className={styles.secondaryButton} onClick={() => setConfirmDealId(null)}>
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{errorMessage}</p>
            <button className={styles.primaryButton} onClick={() => setErrorMessage(null)}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CustomerProfile;
